import type { KeyboardEvent, PointerEvent } from 'react';
import { useRef, useState } from 'react';
import { cn } from 'ui-design-system';
import { Icon } from 'ui-icons';

export type ThresholdRangeStep = {
  value: number;
  label?: string;
  color: string;
};

export type ThresholdDirection = 'row' | 'column';

export type ThresholdRangeProps = {
  title?: string;
  defaultDescription?: string;
  values: ThresholdRangeStep[];
  value: number | undefined;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  initialColor: string;
  name?: string;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  learnMoreUrl?: string;
  direction?: ThresholdDirection;
};

const RAIL_HEIGHT = 4;
const INACTIVE_DOT_SIZE = 8;
const ACTIVE_DOT_SIZE = 16;
const LABEL_GAP = 4;
const LABEL_TOP_OFFSET = RAIL_HEIGHT / 2 + ACTIVE_DOT_SIZE / 2 + LABEL_GAP;

function getSortedSteps(values: ThresholdRangeStep[]) {
  return [...values].sort((a, b) => a.value - b.value);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function isRtlElement(element: Element) {
  return getComputedStyle(element).direction === 'rtl';
}

function getHorizontalRatio(clientX: number, element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
  return isRtlElement(element) ? 1 - ratio : ratio;
}

function getSegments(steps: ThresholdRangeStep[], min: number, initialColor: string) {
  const firstStep = steps[0];
  if (!firstStep) return [];

  const segments = [];

  if (firstStep.value > 0) {
    segments.push({
      key: `initial-${firstStep.value}`,
      startValue: min,
      endValue: firstStep.value,
      fromColor: initialColor,
      toColor: firstStep.color,
      completedStepIndex: 0,
    });
  }

  for (let index = 0; index < steps.length - 1; index += 1) {
    const step = steps[index]!;
    const nextStep = steps[index + 1]!;

    segments.push({
      key: `${step.value}-${nextStep.value}`,
      startValue: step.value,
      endValue: nextStep.value,
      fromColor: step.color,
      toColor: nextStep.color,
      completedStepIndex: index + 1,
    });
  }

  return segments;
}

export function ThresholdRange({
  title,
  defaultDescription,
  values,
  value,
  onChange,
  min = 0,
  max = 100,
  initialColor,
  name,
  onBlur,
  disabled = false,
  className,
  learnMoreUrl,
  direction = 'column',
}: ThresholdRangeProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const hasDraggedRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const steps = getSortedSteps(values);

  if (steps.length === 0) {
    throw new Error('ThresholdRange requires at least one step.');
  }

  const normalizedMax = Math.max(max, steps.at(-1)!.value, 1);
  const firstStep = steps[0]!;
  const lastStep = steps.at(-1)!;
  const activeIndex = steps.findIndex((step) => step.value === value);
  const activeStep = activeIndex >= 0 ? steps[activeIndex] : undefined;
  const segments = getSegments(steps, min, initialColor);

  const selectStepAtIndex = (index: number) => {
    if (disabled) return;
    const nextStep = steps[clamp(index, 0, steps.length - 1)];
    if (!nextStep) return;
    if (nextStep.value !== value) {
      onChange(nextStep.value);
    }
  };

  const selectNearestStep = (clientX: number, element: HTMLDivElement) => {
    if (disabled) return;
    const targetValue = getHorizontalRatio(clientX, element) * normalizedMax;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    steps.forEach((step, index) => {
      const distance = Math.abs(step.value - targetValue);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    selectStepAtIndex(nearestIndex);
  };

  const updateFromPointer = (clientX: number) => {
    const rail = railRef.current;
    if (!rail) return;
    selectNearestStep(clientX, rail);
  };

  const endThumbDrag = (event: PointerEvent<HTMLDivElement>) => {
    const rail = railRef.current;
    if (rail?.hasPointerCapture(event.pointerId)) {
      rail.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
  };

  const handleActiveThumbPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const rail = railRef.current;
    if (!rail) return;
    event.preventDefault();
    event.stopPropagation();
    hasDraggedRef.current = false;
    rail.setPointerCapture(event.pointerId);
    setIsDragging(true);
    updateFromPointer(event.clientX);
  };

  const handleRailPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const rail = railRef.current;
    if (!rail?.hasPointerCapture(event.pointerId)) return;
    hasDraggedRef.current = true;
    updateFromPointer(event.clientX);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    const selectNext = () => {
      event.preventDefault();
      if (activeIndex < 0) {
        selectStepAtIndex(0);
        return;
      }
      selectStepAtIndex(activeIndex + 1);
    };

    const selectPrevious = () => {
      event.preventDefault();
      if (activeIndex < 0) {
        selectStepAtIndex(steps.length - 1);
        return;
      }
      selectStepAtIndex(activeIndex - 1);
    };

    switch (event.key) {
      case 'ArrowUp':
        selectNext();
        return;
      case 'ArrowDown':
        selectPrevious();
        return;
      case 'ArrowRight':
        if (isRtlElement(event.currentTarget)) {
          selectPrevious();
          return;
        }
        selectNext();
        return;
      case 'ArrowLeft':
        if (isRtlElement(event.currentTarget)) {
          selectNext();
          return;
        }
        selectPrevious();
        return;
      case 'Home':
        event.preventDefault();
        selectStepAtIndex(0);
        return;
      case 'End':
        event.preventDefault();
        selectStepAtIndex(steps.length - 1);
        return;
      default:
        return;
    }
  };

  return (
    <div className={cn('flex flex-col gap-md', className)}>
      {name ? <input type="hidden" name={name} value={value ?? ''} /> : null}

      <div className={cn('grid gap-md', direction === 'row' && 'grid-cols-2')}>
        {title ? (
          <div className="flex items-center gap-xs">
            <span className="text-grey-primary text-s font-medium">{title}</span>
            {learnMoreUrl ? (
              <a
                href={learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-grey-50 text-purple-primary hover:text-purple-hover"
              >
                <Icon icon="helpcenter" className="size-4" />
              </a>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-md">
          <div
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-valuemin={firstStep.value}
            aria-valuemax={lastStep.value}
            aria-valuenow={value}
            aria-valuetext={activeStep?.value.toString() ?? ''}
            className={cn(
              'rounded-lg px-xs pb-sm pt-md focus-visible:outline-2 focus-visible:outline-offset-6 focus-visible:outline-purple-primary w-full',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              isDragging && 'select-none',
            )}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
          >
            <div className="px-md pb-sm">
              <div className="relative w-full">
                <div
                  ref={railRef}
                  data-testid="threshold-range-rail"
                  className="bg-grey-border relative w-full rounded-full before:absolute before:inset-x-0 before:-top-5 before:-bottom-5 before:content-['']"
                  style={{ height: `${RAIL_HEIGHT}px` }}
                  onClick={(event) => {
                    if (hasDraggedRef.current) {
                      hasDraggedRef.current = false;
                      return;
                    }
                    selectNearestStep(event.clientX, event.currentTarget);
                  }}
                  onPointerMove={handleRailPointerMove}
                  onPointerUp={endThumbDrag}
                  onPointerCancel={endThumbDrag}
                >
                  {segments.map((segment) => {
                    const start = (segment.startValue / normalizedMax) * 100;
                    const end = (segment.endValue / normalizedMax) * 100;
                    const width = end - start;
                    const isCompleted = activeIndex >= segment.completedStepIndex;
                    return (
                      <div
                        key={segment.key}
                        aria-hidden="true"
                        className={cn('absolute rounded-full', isCompleted ? 'rtl:-scale-x-100' : 'bg-grey-border')}
                        style={{
                          insetInlineStart: `${start}%`,
                          width: `${width}%`,
                          height: `${RAIL_HEIGHT}px`,
                          background: isCompleted
                            ? `linear-gradient(to right, ${segment.fromColor} 0%, ${segment.toColor} 100%)`
                            : undefined,
                        }}
                      />
                    );
                  })}

                  {steps.map((step, index) => {
                    const position = (step.value / normalizedMax) * 100;
                    const isActive = index === activeIndex;
                    const isCompleted = activeIndex >= index;
                    const size = isActive ? ACTIVE_DOT_SIZE : INACTIVE_DOT_SIZE;
                    const backgroundColor = isCompleted ? step.color : 'var(--color-grey-border)';

                    return (
                      <button
                        key={step.value}
                        type="button"
                        tabIndex={-1}
                        aria-label={`${title} ${step.value.toString()}`}
                        disabled={disabled}
                        data-testid={isActive ? 'threshold-range-thumb-active' : undefined}
                        className={cn(
                          'absolute top-1/2 -translate-y-1/2 rounded-full border-0 p-0 touch-none',
                          disabled
                            ? 'cursor-not-allowed'
                            : isActive
                              ? isDragging
                                ? 'cursor-grabbing'
                                : 'cursor-grab'
                              : 'cursor-pointer',
                        )}
                        style={{
                          insetInlineStart: `${position}%`,
                          marginInlineStart: `-${size / 2}px`,
                          width: `${size}px`,
                          height: `${size}px`,
                          backgroundColor,
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (hasDraggedRef.current) {
                            hasDraggedRef.current = false;
                            return;
                          }
                          selectStepAtIndex(index);
                        }}
                        onPointerDown={isActive ? handleActiveThumbPointerDown : undefined}
                      />
                    );
                  })}
                </div>

                {activeStep ? (
                  <div
                    className="absolute text-s leading-none font-medium pointer-events-none -translate-x-1/2 rtl:translate-x-1/2"
                    style={{
                      top: `${LABEL_TOP_OFFSET}px`,
                      insetInlineStart: `${(activeStep.value / normalizedMax) * 100}%`,
                      color: activeStep.color,
                    }}
                  >
                    {activeStep.value.toString()}
                  </div>
                ) : null}

                {activeStep?.value !== lastStep.value ? (
                  <div
                    className="text-grey-placeholder absolute text-s leading-none font-medium pointer-events-none -translate-x-1/2 rtl:translate-x-1/2"
                    style={{
                      top: `${LABEL_TOP_OFFSET}px`,
                      insetInlineStart: `${(lastStep.value / normalizedMax) * 100}%`,
                    }}
                  >
                    {lastStep.value.toString()}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {activeStep?.label || defaultDescription ? (
            <p className="text-grey-placeholder text-xs leading-tight">{activeStep?.label ?? defaultDescription}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
